(function () {
  'use strict';

  const PIN_PARAM = 'pin';
  const MAX_PINS = 50;
  const FETCH_BATCH_SIZE = 4;
  const PENDING_CLASS = 'is-pin-pending';

  const selectors = {
    root: '[data-pin-root]',
    productsGrid: '[data-products-grid]',
    grid: '.grid',
    item: '[data-grid-item]',
  };

  function getPinnedHandles() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(PIN_PARAM);
    if (!raw) return [];

    // Sorting or filtering means the visitor chose their own order
    for (const key of params.keys()) {
      if (key === 'sort_by' || key.startsWith('filter.')) return [];
    }

    const handles = raw
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);

    return Array.from(new Set(handles)).slice(0, MAX_PINS);
  }

  function getCurrentPage() {
    const page = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    return page > 0 ? page : 1;
  }

  function getItemHandle(item) {
    const match = (item.dataset.url || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]).toLowerCase() : null;
  }

  function buildPageUrl(page, sectionId) {
    const params = new URLSearchParams(window.location.search);
    params.delete(PIN_PARAM);
    params.set('page', page);
    params.set('section_id', sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  async function fetchItemsFromPage(page, sectionId, handles) {
    try {
      const response = await fetch(buildPageUrl(page, sectionId));
      if (!response.ok) return [];
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll(selectors.item)).filter((item) => handles.includes(getItemHandle(item)));
    } catch (error) {
      return [];
    }
  }

  async function findMissingItems(missing, totalPages, sectionId) {
    const found = new Map();

    for (let start = 2; start <= totalPages && found.size < missing.length; start += FETCH_BATCH_SIZE) {
      const pages = [];
      for (let page = start; page < start + FETCH_BATCH_SIZE && page <= totalPages; page++) {
        pages.push(page);
      }

      const results = await Promise.all(pages.map((page) => fetchItemsFromPage(page, sectionId, missing)));
      results.flat().forEach((item) => {
        const handle = getItemHandle(item);
        if (!found.has(handle)) {
          found.set(handle, document.importNode(item, true));
        }
      });
    }

    return found;
  }

  class CollectionPin {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.totalPages = parseInt(root.dataset.pinPages, 10) || 1;
      this.productsGrid = root.querySelector(selectors.productsGrid);
      this.handles = [];
      this.runId = 0;

      if (!this.productsGrid) {
        this.reveal();
        return;
      }

      this.observer = new MutationObserver((records) => this.onMutation(records));
      this.observer.observe(this.productsGrid, {childList: true, subtree: true});

      this.apply();
    }

    reveal() {
      this.root.classList.remove(PENDING_CLASS);
    }

    getGrid() {
      return this.productsGrid.querySelector(selectors.grid);
    }

    removeDuplicates(nodes) {
      if (!this.handles.length) return;

      nodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const items = node.matches(selectors.item) ? [node] : node.querySelectorAll(selectors.item);
        items.forEach((item) => {
          if (!item.hasAttribute('data-pinned') && this.handles.includes(getItemHandle(item))) {
            item.remove();
          }
        });
      });
    }

    onMutation(records) {
      // The filter form swaps the whole grid; infinite scroll only appends into it
      const gridReplaced = records.some((record) => record.target === this.productsGrid);
      if (gridReplaced) {
        this.apply();
        return;
      }

      records.forEach((record) => this.removeDuplicates(record.addedNodes));
    }

    async apply() {
      const runId = ++this.runId;
      this.handles = getPinnedHandles();
      const grid = this.getGrid();

      if (!this.handles.length || !grid) {
        this.reveal();
        return;
      }

      if (getCurrentPage() > 1) {
        this.removeDuplicates([grid]);
        this.reveal();
        return;
      }

      const pinned = new Map();
      grid.querySelectorAll(selectors.item).forEach((item) => {
        const handle = getItemHandle(item);
        if (!this.handles.includes(handle)) return;
        if (pinned.has(handle)) {
          item.remove();
        } else {
          pinned.set(handle, item);
        }
      });

      const missing = this.handles.filter((handle) => !pinned.has(handle));
      if (missing.length && this.totalPages > 1) {
        const fetched = await findMissingItems(missing, this.totalPages, this.sectionId);
        if (runId !== this.runId) return;
        fetched.forEach((item, handle) => pinned.set(handle, item));
      }

      const ordered = this.handles.filter((handle) => pinned.has(handle)).map((handle) => pinned.get(handle));
      ordered.forEach((item) => item.setAttribute('data-pinned', ''));

      const currentGrid = this.getGrid();
      if (currentGrid && ordered.length) {
        currentGrid.prepend(...ordered);
      }

      this.reveal();
    }
  }

  function init() {
    document.querySelectorAll(selectors.root).forEach((root) => {
      if (!root.collectionPin) {
        root.collectionPin = new CollectionPin(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
